import http from '@/utils/http';
import { normalizePcConsoleTopChartsApiPayload } from '@intelligence/pages/TopCharts/_config/topchartFilterCfg';
import {
  isSteamPlayersByCountryMetric,
  STEAM_PLAYERS_BY_COUNTRY_DAILY_GRANULARITY,
} from '@intelligence/pages/TopCharts/_config/playersByCountryMetrics';

const ALINEA_WISHLISTS_BY_COUNTRY_METRIC = 'alinea__wishlists_by_country';

/**
 * Steam Sales 数据适配器
 * 用于将 Steam Sales 相关接口的参数和响应格式转换为统一的 pcConsoleMetricData 接口格式
 */
class SteamSalesAdapter {
    // 常量定义
    private static readonly DEFAULT_MARKET = 'global';
    private static readonly DEFAULT_SEGMENT = 'All';
    private static readonly DEFAULT_SUM_AVG = [
        { date: 'avg', metrics: null },
        { date: 'sum', metrics: null },
        { date: 'max', metrics: null },
    ];
    // Stats 页面 mscience 支持的 segment 列表
    private static readonly MSCIENCE_SUPPORTED_SEGMENTS = [
        'Full Game', 'MTX', 'DLC: All Other', 'DLC: Expansion',
        'DLC: Content Pre-Purchase Pass', 'DLC: Unlockable Content Pass', 'Subscription'
    ];
    // Stats 页面平台映射配置（参考 KPI Trends 页面的 platformCascader.ts）
    private static readonly PLATFORM_MAP_BY_SOURCE: Record<string, Record<string, string[]>> = {
        mscience: { PC: ['PC'], PlayStation: ['PlayStation'], Xbox: ['Xbox'], Nintendo: ['Nintendo'] },
        gsd: { PC: ['PC'], PlayStation: ['PlayStation 4', 'PlayStation 5'], Xbox: ['Xbox Series X|S', 'Xbox One'], Nintendo: ['Nintendo Switch'] },
        gamalytic: { PC: ['PC'] },
        npd: { PC: ['PC'], PlayStation: ['PlayStation 4', 'PlayStation 5'], Xbox: ['Xbox Series X|S', 'Xbox One'], Nintendo: ['Nintendo Switch', 'WII U'] },
        ampere: { PC: ['PC'], PlayStation: ['PlayStation'], Xbox: ['Xbox'] },
        alinea: { PC: ['PC'], PlayStation: ['PlayStation'], Xbox: ['Xbox'] },
        newzoo: { PC: ['PC'], PlayStation: ['PlayStation 4', 'PlayStation 5'], Xbox: ['Xbox Series X|S', 'Xbox One'], Nintendo: ['Nintendo Switch'] },
    };

    /**
     * 将数组或单个值转换为数组
     */
    private static toArray<T>(value: T | T[] | undefined | null): T[] {
        if (!value) return [];
        return Array.isArray(value) ? value : [value];
    }

    /**
     * 根据数据源获取对应的平台值
     */
    private static getPlatformsForSource(dataSource: string, platforms: string[]): string[] {
        const mapping = this.PLATFORM_MAP_BY_SOURCE[dataSource];
        if (!mapping || !platforms?.length) return [];
        return platforms.flatMap((p) => mapping[p] || []);
    }

    /**
     * 按日期排序
     */
    private static sortByDate<T extends { date: string }>(items: T[]): T[] {
        return [...items].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }

    /**
     * 从 sum_avg 的 sum 行提取各 metric 的 value / growth_ratio
     */
    private static buildSumMetricsMap(sumAvg: any[] = []) {
        const sumMetrics = sumAvg.find((item: any) => item?.date === 'sum')?.metrics || [];
        const valueByMetric: Record<string, number | null> = {};
        const growthRatioByMetric: Record<string, number | null> = {};

        sumMetrics.forEach((metricItem: any) => {
            const metricName = metricItem?.metric || metricItem?.name;
            if (!metricName) return;
            valueByMetric[metricName] = metricItem?.value ?? null;
            growthRatioByMetric[metricName] =
                metricItem?.growth_ratio ?? metricItem?.dod ?? null;
        });

        return { valueByMetric, growthRatioByMetric };
    }

    /**
     * 统一 sum_avg metrics 的 dod / growth_ratio 字段
     */
    private static normalizeSumAvgMetrics(sumAvg: any[] = []) {
        return sumAvg.map((item: any) => {
            if (!Array.isArray(item?.metrics)) return item;
            return {
                ...item,
                metrics: item.metrics.map((metricItem: any) => {
                    const growthRatio =
                        metricItem?.growth_ratio ?? metricItem?.dod ?? null;
                    return {
                        ...metricItem,
                        growth_ratio: growthRatio,
                        dod: growthRatio,
                    };
                }),
            };
        });
    }

    /**
     * 从 metric_value_list 创建 metric 对象
     */
    private static createMetricItem(item: any, market?: string) {
        const baseMetric = {
            metric: item.metric || '',
            name: item.metric || '',
            value: item.value,
            dod: item.growth_ratio || null,
        };
        return market ? { ...baseMetric, market } : baseMetric;
    }

    /**
     * 处理 gamalytic_ 前缀的 metrics，自动设置 data_sources
     */
    static processGamalyticMetrics(payload: any): any {
        const processedPayload = { ...payload };
        if (Array.isArray(processedPayload.metrics)) {
            const hasGamalyticMetrics = processedPayload.metrics.some((m: string) => m?.startsWith('gamalytic_'));
            if (hasGamalyticMetrics) {
                processedPayload.data_sources = ['gamalytic'];
            }
        }
        return processedPayload;
    }

    /**
     * 构建 metric_condition 对象
     * Stats 页面没有按数据源分类的 segment 筛选器，需要根据 segment 值分配给不同的数据源
     * - gsd 始终要传递，没选 Full Game 时传 segments: []
     * - mscience 根据选择的 segment 传递
     * - 当选择 "All" 时，gsd 传 ["Full Game"]，mscience 传 ["All"]
     * - platforms 需要为每个数据源添加对应的平台信息（不同数据源支持的平台和值不同）
     */
    private static buildMetricCondition(
        segmentsValue: string[],
        platforms?: string[]
    ): Record<string, any> {
        const metricCondition: Record<string, any> = {};

        // 处理 segments
        if (segmentsValue.includes('All')) {
            metricCondition.gsd = { segments: ['Full Game'] };
            metricCondition.mscience = { segments: ['All'] };
        } else {
            metricCondition.gsd = { segments: segmentsValue.includes('Full Game') ? ['Full Game'] : [] };
            const mscienceSegments = segmentsValue.filter((seg) => this.MSCIENCE_SUPPORTED_SEGMENTS.includes(seg));
            if (mscienceSegments.length > 0) {
                const isMscienceAllSelected = mscienceSegments.length === this.MSCIENCE_SUPPORTED_SEGMENTS.length;
                metricCondition.mscience = { segments: isMscienceAllSelected ? ['All'] : mscienceSegments };
            }
        }

        // 处理 platforms
        if (platforms?.length) {
            // 辅助函数：为数据源添加平台配置
            const addPlatforms = (source: string, fieldName: string, extra?: Record<string, any>) => {
                const values = this.getPlatformsForSource(source, platforms);
                if (values.length > 0) {
                    metricCondition[source] = { ...metricCondition[source], [fieldName]: values, ...extra };
                }
            };

            addPlatforms('mscience', 'platforms');
            addPlatforms('gsd', 'device', { gsd_types: ['NETWORK'] });
            addPlatforms('gamalytic', 'platform');
            addPlatforms('npd', 'device');
            addPlatforms('ampere', 'platforms');
            addPlatforms('alinea', 'platforms');
            addPlatforms('newzoo', 'device');
            metricCondition.vginsights = { platforms: [] };
        }

        return metricCondition;
    }

  /**
   * 转换combine详情页Stats、Countries标签页Steam Data子页面接口入参
   * @param payload - 页面参数
   * @param isMarket - 是否为Countries子页面
   */
    static convertSteamSalesTrendsParams (payload: any, isMarket = false): any {
      const {
        period,
        date_type,
        start_date,
        end_date,
        data_sources,
        entity_type,
        metrics,
        edition_id,
        unified_ids,
        gameId,
        b_group_by_device,
        device,
        regions,
        segment,
        segment_list,
      } = payload;

      // 构建 source_metrics 对象
      const sourceMetrics: any = {};
      const dataSources = this.toArray(data_sources);
      const metricsList = this.toArray(metrics);

      // 将 metrics 按 data_source 分组
      if (dataSources.length > 0 && metricsList.length > 0) {
        dataSources.forEach((source: string) => {
          sourceMetrics[source] = metricsList;
        });
      }

      // 确定 game_id
      const gameIdValue = unified_ids?.[0] || edition_id || gameId;

      // 验证必需参数
      if (!gameIdValue) {
        throw new Error('Missing required parameter: gameId (unified_ids, edition_id, or gameId)');
      }

      if (Object.keys(sourceMetrics).length === 0) {
        throw new Error('Missing required parameter: source_metrics (data_sources and metrics)');
      }

      if (!entity_type) {
        throw new Error('Missing required parameter: entity_type');
      }

      // 构建转换后的参数
      const rawGranularity = period || date_type || 'monthly';
      const granularity = metricsList.some(
        (metric) =>
          isSteamPlayersByCountryMetric(metric) ||
          metric === ALINEA_WISHLISTS_BY_COUNTRY_METRIC,
      )
        ? STEAM_PLAYERS_BY_COUNTRY_DAILY_GRANULARITY
        : rawGranularity;

      const convertedParams: any = {
        ids: [gameIdValue],
        granularity,
        start_date: start_date || '',
        end_date: end_date || '',
        source_metrics: sourceMetrics,
        entity_type: Array.isArray(entity_type) ? entity_type[0] : entity_type,
        b_true_data: true,
        b_group_by_date: true,
        b_growth_ratio: true,
        b_sum_avg_show: true,
        b_group_by_market: isMarket, // countries 页面需要按市场分组
        b_group_by_region: false,
        b_group_by_platform: false,
        b_group_by_segment: false,
        start_release_time: 0,
        end_release_time: 0,
        adjust_metric: payload?.adjust_metric ?? true,
      };

      // 处理 device/platforms
      const platformsValue = device ? this.toArray(device) : [];
      if (platformsValue.length > 0) {
        convertedParams.platforms = platformsValue;
      } else if (b_group_by_device !== undefined) {
        convertedParams.b_group_by_device = b_group_by_device;
      }

      // 处理 regions/markets
      convertedParams.markets = regions ? this.toArray(regions) : [this.DEFAULT_MARKET];

      // 处理 segment
      const segmentsValue = segment_list ? this.toArray(segment_list) : segment ? [segment] : [this.DEFAULT_SEGMENT];
      convertedParams.segments = segmentsValue;

      // 处理 metric_condition（包含 segments 和 platforms）
      convertedParams.metric_condition = this.buildMetricCondition(segmentsValue, platformsValue);

      return convertedParams;
    }

    /**
     * 将 salesTrend/salesMarket 的参数格式转换为 pcConsoleMetricData 接口的参数格式并调用接口
     * @param payload - 原始参数对象
     * @param isMarket - 是否为市场数据（countries 页面需要按市场分组）
     * @returns API 响应数据
     */
    static async convertParamsAndFetch(payload: any, isMarket: boolean = false): Promise<any> {
        const convertedParams = this.convertSteamSalesTrendsParams(payload, isMarket);

        // 调用 pcConsoleMetricData 接口
        const response = await http({
            url: '/api/v1/intelligence_pc/compare/pcConsoleMetricData',
            method: 'post',
            data: normalizePcConsoleTopChartsApiPayload(convertedParams),
            rawData: true,
        }) as Promise<any>;

        return response;
    }

    /**
     * 将 pcConsoleMetricData 接口返回格式转换为 salesTrend 接口返回格式
     * @param response - pcConsoleMetricData 接口的响应数据
     * @returns 转换后的 salesTrend 格式数据
     */
    static convertToSalesTrendFormat(response: any): any {
        if (!response || !response.data || !Array.isArray(response.data) || response.data.length === 0) {
            return {
                ...response,
                data: {
                    trend: null,
                    sum_avg: [],
                    total: [],
                    device_trends: null,
                    device_pie: null,
                    metric_total: 0,
                },
            };
        }

        const gameData = response.data[0];
        const metricValueList = gameData?.metric_value_list || [];
        const sumAvg = this.normalizeSumAvgMetrics(gameData?.sum_avg || []);

        // 将 metric_value_list 转换为 trend 格式（按 date 分组）
        const trendMap: { [key: string]: any[] } = {};
        metricValueList.forEach((item: any) => {
            const date = item.date;
            if (!trendMap[date]) {
                trendMap[date] = [];
            }
            trendMap[date].push({
                ...this.createMetricItem(item),
                growth_ratio: item.growth_ratio || 0,
            });
        });

        // 转换为 trend 数组格式并排序
        const trend = this.sortByDate(
            Object.keys(trendMap).map((date) => ({
                date,
                metrics: trendMap[date],
            })),
        );

        // 构建 total 数据（从 sum_avg 中提取 sum 数据）
        const total: any[] = [];
        sumAvg.forEach((item: any) => {
            if (item.date === 'sum' && Array.isArray(item.metrics)) {
                item.metrics.forEach((metricItem: any) => {
                    const growthRatio =
                        metricItem?.growth_ratio ?? metricItem?.dod ?? null;
                    total.push({
                        metric: metricItem.metric,
                        name: metricItem.name || metricItem.metric,
                        opt: 'sum',
                        value: metricItem.value,
                        growth_ratio: growthRatio,
                        dod: growthRatio,
                    });
                });
            }
        });

        return {
            ...response,
            data: {
                trend: trend.length > 0 ? trend : null,
                sum_avg: sumAvg,
                total: total,
                device_trends: null,
                device_pie: null,
                metric_total: 0,
            },
        };
    }

    /**
     * 将 pcConsoleMetricData 接口返回格式转换为 salesMarket 接口返回格式
     * @param response - pcConsoleMetricData 接口的响应数据
     * @returns 转换后的 salesMarket 格式数据
     */
    static convertToSalesMarketFormat(response: any): any {
        if (!response || !response.data || !Array.isArray(response.data) || response.data.length === 0) {
            return {
                ...response,
                data: {
                    table: [],
                    trend: [],
                    device_trend: null,
                    sum_avg: this.DEFAULT_SUM_AVG,
                    device_list: null,
                    time: '',
                    players_country_table: null,
                    players_country_charts: null,
                    metric_total: 0,
                },
            };
        }

        const gameData = response.data[0];
        const metricValueList = gameData?.metric_value_list || [];
        const sumAvg = this.normalizeSumAvgMetrics(gameData?.sum_avg || []);
        const { valueByMetric, growthRatioByMetric } = this.buildSumMetricsMap(sumAvg);
        const req = response?.req || {};

        // countries / players_by_country 这类接口返回：metric_value_list 按 market 给出值（无 date 字段）
        // 页面需要 players_country_table + players_country_charts（均为 { market, name/name_cn, metrics: [{ name, value, dod }] } 结构）
        const isGroupByMarket = !!req?.b_group_by_market;
        const hasMarketValueList = Array.isArray(metricValueList) && metricValueList.some((item: any) => !!item?.market);
        const hasDateValueList = Array.isArray(metricValueList) && metricValueList.some((item: any) => !!item?.date);

        if (isGroupByMarket && hasMarketValueList && !hasDateValueList) {
          const playersCountryList = metricValueList
            .filter((item: any) => item?.market)
            .map((item: any) => ({
              market: item.market,
              name: item.market, // 兜底：无本地化映射时直接展示后端 market
              name_cn: item.market_cn || item.market,
              metrics: [
                {
                  name: item.metric || '',
                  metric: item.metric || '',
                  value: item.value,
                  dod: item.growth_ratio ?? item.dod ?? null,
                },
              ],
            }))
            .sort((a: any, b: any) => (b?.metrics?.[0]?.value ?? 0) - (a?.metrics?.[0]?.value ?? 0));

          // 顶部固定行：Global = 100%
          // 该指标本质为“国家占比”，Global 行用于展示总占比（始终为 100%）
          const metricKey = metricValueList?.[0]?.metric || '';
          playersCountryList.unshift({
            market: this.DEFAULT_MARKET,
            name: 'Global',
            name_cn: '全球',
            metrics: [
              {
                name: metricKey,
                metric: metricKey,
                value: 100,
                dod: null,
              },
            ],
          });

          return {
            ...response,
            data: {
              trend: [],
              table: [],
              device_trend: null,
              sum_avg: sumAvg.length > 0 ? sumAvg : this.DEFAULT_SUM_AVG,
              device_list: null,
              // time 用于前端柱状图上显示的“最新时间”
              time: req?.end_date || '',
              players_country_table: playersCountryList,
              players_country_charts: playersCountryList,
              metric_total: 0,
            },
          };
        }

        // 将 metric_value_list 转换为 trend 格式（按 date 分组，每个 date 有 metrics 数组）
        const historyMap: { [key: string]: any[] } = {};
        metricValueList.forEach((item: any) => {
            const date = item.date;
            if (!historyMap[date]) {
                historyMap[date] = [];
            }
            historyMap[date].push(this.createMetricItem(item, this.DEFAULT_MARKET));
        });

        // 转换为 history 数组格式并排序
        const history = this.sortByDate(
            Object.keys(historyMap).map((date) => ({
                date,
                metrics: historyMap[date],
            })),
        );

        // 构建 trend 数组（按 market 分组）
        const trend = [
            {
                market: this.DEFAULT_MARKET,
                name: '',
                history,
            },
        ];

        // 将 metric_value_list 转换为 table 格式（汇总数据：按 metric 求和）
        const metricTotalMap: { [key: string]: number } = {};
        metricValueList.forEach((item: any) => {
            const metric = item.metric || '';
            if (metric && item.value != null) {
                metricTotalMap[metric] = (metricTotalMap[metric] || 0) + item.value;
            }
        });

        // 构建 table 数组（value / 环比优先取 sum_avg.sum）
        const table = Object.keys(metricTotalMap).map((metric) => ({
            market: this.DEFAULT_MARKET,
            name: '',
            metrics: [
                this.createMetricItem(
                    {
                        metric,
                        value: valueByMetric[metric] ?? metricTotalMap[metric],
                        growth_ratio: growthRatioByMetric[metric] ?? null,
                    },
                    this.DEFAULT_MARKET,
                ),
            ],
        }));

        // 直接复制 sum_avg，如果没有数据则使用默认格式
        const formattedSumAvg = sumAvg.length > 0 ? sumAvg : this.DEFAULT_SUM_AVG;

        // 计算 metric_total（优先从 sum_avg 中提取 sum 值，否则从 table 中计算）
        const sumItem = formattedSumAvg.find((item: any) => item.date === 'sum');
        const metricTotal = sumItem?.metrics?.[0]?.value ?? table[0]?.metrics?.[0]?.value ?? 0;

        return {
            ...response,
            data: {
                trend: trend.length > 0 ? trend : [],
                table: table,
                device_trend: null,
                sum_avg: formattedSumAvg,
                device_list: null,
                time: '',
                players_country_table: null,
                players_country_charts: null,
                metric_total: metricTotal,
            },
        };
    }


    /**
     * 将 AllMetricsTrends 参数格式转换为 pcConsoleMetricData 接口的参数格式
     * @param payload - AllMetricsTrends 原始参数对象
     * @returns 转换后的 pcConsoleMetricData 参数
     */
    static convertAllMetricsTrendsParams(payload: any): any {
        const {
            unified_ids,
            entity_type,
            region_type,
            regions,
            date_type,
            segment,
            start_date,
            end_date,
            device,
            source_metrics,
            data_method,
            segment_list,
        } = payload;

        // 确定 game_id
        const gameIdValue = unified_ids?.[0];

        // 验证必需参数
        if (!gameIdValue) {
            throw new Error('Missing required parameter: unified_ids');
        }

        if (!entity_type) {
            throw new Error('Missing required parameter: entity_type');
        }

        if (!source_metrics || Object.keys(source_metrics).length === 0) {
            throw new Error('Missing required parameter: source_metrics');
        }

        // 构建转换后的参数
        const convertedParams: any = {
            ids: this.toArray(unified_ids),
            granularity: date_type || 'monthly',
            start_date: start_date || '',
            end_date: end_date || '',
            source_metrics: source_metrics,
            entity_type: Array.isArray(entity_type) ? entity_type[0] : entity_type,
            b_true_data: true,
            b_group_by_date: true,
            b_growth_ratio: true,
            b_sum_avg_show: true,
            b_group_by_market: false,
            b_group_by_region: false,
            b_group_by_platform: false,
            b_group_by_segment: false,
            start_release_time: 0,
            end_release_time: 0,
            adjust_metric: payload?.adjust_metric ?? true,
        };

        // 处理 device/platforms
        const platformsValue = (device && Array.isArray(device) && device.length > 0) ? device : [];
        if (platformsValue.length > 0) {
            convertedParams.platforms = platformsValue;
        }

        // 处理 regions/markets
        convertedParams.markets = regions ? this.toArray(regions) : [this.DEFAULT_MARKET];

        // 处理 segment
        const segmentsValue = segment_list ? this.toArray(segment_list) : segment ? [segment] : [this.DEFAULT_SEGMENT];
        convertedParams.segments = segmentsValue;

        // 处理 metric_condition（包含 segments 和 platforms）
        convertedParams.metric_condition = this.buildMetricCondition(segmentsValue, platformsValue);

        return convertedParams;
    }

    /**
     * 将 pcConsoleMetricData 接口返回格式转换为 AllMetricsTrends 接口返回格式
     * AllMetricsTrends 格式使用 trends (复数) 而不是 trend (单数)
     * @param response - pcConsoleMetricData 接口的响应数据
     * @returns 转换后的 AllMetricsTrends 格式数据
     */
    static convertToAllMetricsTrendsFormat(response: any): any {
        if (!response || !response.data || !Array.isArray(response.data) || response.data.length === 0) {
            return {
                ...response,
                data: {
                    device_trends: null,
                    trends: null,
                    total: null,
                    device_pie: null,
                    sum_avg: [
                        { date: 'sum', metrics: null },
                        { date: 'avg', metrics: null },
                    ],
                },
            };
        }

        const gameData = response.data[0];
        const metricValueList = gameData?.metric_value_list || [];
        const sumAvg = this.normalizeSumAvgMetrics(gameData?.sum_avg || []);

        // 将 metric_value_list 转换为 trends 格式（按 date 分组）
        const trendMap: { [key: string]: any[] } = {};
        metricValueList.forEach((item: any) => {
            const date = item.date;
            if (!trendMap[date]) {
                trendMap[date] = [];
            }
            trendMap[date].push({
                ...this.createMetricItem(item),
                growth_ratio: item.growth_ratio || 0,
            });
        });

        // 转换为 trends 数组格式并排序
        const trends = this.sortByDate(
            Object.keys(trendMap).map((date) => ({
                date,
                metrics: trendMap[date],
            })),
        );

        // 处理 sum_avg 格式（确保有 sum 和 avg）
        const formattedSumAvg = [
            { date: 'sum', metrics: null },
            { date: 'avg', metrics: null },
        ];

        sumAvg.forEach((item: any) => {
            if (item.date === 'sum' && Array.isArray(item.metrics)) {
                formattedSumAvg[0].metrics = item.metrics;
            } else if (item.date === 'avg' && Array.isArray(item.metrics)) {
                formattedSumAvg[1].metrics = item.metrics;
            }
        });

        return {
            ...response,
            data: {
                device_trends: null,
                trends: trends.length > 0 ? trends : null, // 注意：使用 trends (复数)
                total: null,
                device_pie: null,
                sum_avg: formattedSumAvg,
            },
        };
    }

    /**
     * 获取 AllMetricsTrends 数据（完整流程：参数转换 + API 调用 + 响应转换）
     * @param payload - 原始参数对象
     * @returns 转换后的 AllMetricsTrends 格式数据
     */
    static async fetchAllMetricsTrends(payload: any): Promise<any> {
        // 转换参数格式
        const convertedParams = this.convertAllMetricsTrendsParams(payload);

        const response = await http({
            url: '/api/v1/intelligence_pc/compare/pcConsoleMetricData',
            method: 'post',
            data: normalizePcConsoleTopChartsApiPayload(convertedParams),
            rawData: true,
        }) as Promise<any>;

        // 转换响应格式
        return this.convertToAllMetricsTrendsFormat(response);
    }

    /**
   * 获取 Steam Sales Trend 数据（完整流程：参数转换 + API 调用 + 响应转换）
   * @param payload - 原始参数对象
   * @returns 转换后的 salesTrend 格式数据
   */
    static async fetchSalesTrend(payload: any): Promise<any> {
        const processedPayload = this.processGamalyticMetrics(payload);
        const response = await this.convertParamsAndFetch(processedPayload, false);
        return this.convertToSalesTrendFormat(response);
    }

    /**
     * 获取 Steam Sales Market 数据（完整流程：参数转换 + API 调用 + 响应转换）
     * @param payload - 原始参数对象
     * @returns 转换后的 salesMarket 格式数据
     */
    static async fetchSalesMarket(payload: any): Promise<any> {
        const processedPayload = this.processGamalyticMetrics(payload);
        const response = await this.convertParamsAndFetch(processedPayload, true);
        return this.convertToSalesMarketFormat(response);
    }
}

export default SteamSalesAdapter;
